/**
 * BoundingBoxSplitter Utility
 *
 * Provides utilities for splitting bounding boxes into quadrants for parallel API requests.
 * Used to optimize observation data fetching by dividing large geographic areas.
 */

/**
 * Represents a geographic bounding box defined by latitude and longitude coordinates.
 */
export interface BoundingBox {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
}

/**
 * Default target tile size in square degrees.
 * Smaller tiles = more requests but more reliable.
 * ~100 sq deg = roughly 10° x 10° tiles.
 * This helps avoid 500/503 errors from the DTN API.
 */
export const DEFAULT_TARGET_TILE_SIZE = 100;

/**
 * Maximum number of tiles to split into.
 * 16 tiles (2 split levels) caps the request storm at world zoom
 * while leaving typical zoom (≤4 tiles) completely unaffected.
 * ponytail: was 64 — reduced to prevent 64-request bursts at zoom≤3
 * tripping the server's 100 req/60s limit. Ceiling: zoom-adaptive
 * MAX_TILES if even 16 tiles at world zoom causes issues.
 */
export const MAX_TILES = 16;

/**
 * Calculate the area of a bounding box in square degrees.
 *
 * @param bbox - The bounding box to calculate area for
 * @returns The area in square degrees
 */
export function calculateBoundingBoxArea(bbox: BoundingBox): number {
  const latRange = bbox.maxLat - bbox.minLat;
  const lonRange = bbox.maxLon - bbox.minLon;
  return latRange * lonRange;
}

/**
 * Split a bounding box into 4 equal quadrants.
 * Returns array of 4 bounding boxes: [NW, NE, SW, SE]
 *
 * Splitting Logic:
 * ```
 * Original:
 * +-------------------+
 * |                   |
 * |                   |
 * |                   |
 * +-------------------+
 *
 * Split into 4 quadrants:
 * +--------+--------+
 * |   NW   |   NE   |
 * +--------+--------+
 * |   SW   |   SE   |
 * +--------+--------+
 * ```
 *
 * @param bbox - The bounding box to split
 * @returns Array of 4 bounding boxes: [NW, NE, SW, SE]
 */
export function splitBoundingBox(bbox: BoundingBox): BoundingBox[] {
  const midLat = (bbox.minLat + bbox.maxLat) / 2;
  const midLon = (bbox.minLon + bbox.maxLon) / 2;

  // NW quadrant: upper-left
  const nw: BoundingBox = {
    minLat: midLat,
    maxLat: bbox.maxLat,
    minLon: bbox.minLon,
    maxLon: midLon,
  };

  // NE quadrant: upper-right
  const ne: BoundingBox = {
    minLat: midLat,
    maxLat: bbox.maxLat,
    minLon: midLon,
    maxLon: bbox.maxLon,
  };

  // SW quadrant: lower-left
  const sw: BoundingBox = {
    minLat: bbox.minLat,
    maxLat: midLat,
    minLon: bbox.minLon,
    maxLon: midLon,
  };

  // SE quadrant: lower-right
  const se: BoundingBox = {
    minLat: bbox.minLat,
    maxLat: midLat,
    minLon: midLon,
    maxLon: bbox.maxLon,
  };

  return [nw, ne, sw, se];
}

/**
 * Recursively split a bounding box until all tiles are below the target size.
 * This enables efficient parallel fetching by creating many small tiles
 * that can be fetched concurrently using the API's rate limit.
 *
 * @param bbox - The bounding box to split
 * @param targetTileSize - Target area for each tile in square degrees (default: 100)
 * @param maxTiles - Maximum number of tiles to create (default: 64)
 * @returns Array of bounding boxes, each below the target size
 */
export function splitBoundingBoxRecursively(
  bbox: BoundingBox,
  targetTileSize: number = DEFAULT_TARGET_TILE_SIZE,
  maxTiles: number = MAX_TILES
): BoundingBox[] {
  const area = calculateBoundingBoxArea(bbox);
  
  // If already small enough, return as-is
  if (area <= targetTileSize) {
    return [bbox];
  }
  
  // Calculate how many splits we need
  // Each split quadruples the number of tiles (4^n)
  // We want: area / (4^n) <= targetTileSize
  // So: n >= log4(area / targetTileSize)
  const splitsNeeded = Math.ceil(Math.log(area / targetTileSize) / Math.log(4));
  
  // Calculate resulting tile count and cap at maxTiles
  const resultingTiles = Math.pow(4, splitsNeeded);
  const actualSplits = resultingTiles > maxTiles 
    ? Math.floor(Math.log(maxTiles) / Math.log(4))
    : splitsNeeded;
  
  // Perform recursive splitting
  let tiles: BoundingBox[] = [bbox];
  
  for (let i = 0; i < actualSplits; i++) {
    const newTiles: BoundingBox[] = [];
    for (const tile of tiles) {
      newTiles.push(...splitBoundingBox(tile));
    }
    tiles = newTiles;
  }
  
  if (import.meta.env.DEV) console.log(`[BoundingBoxSplitter] Split ${area.toFixed(0)} sq deg into ${tiles.length} tiles (target: ${targetTileSize} sq deg)`);
  
  return tiles;
}

/**
 * Determine if a bounding box should be split based on area threshold.
 * @param bbox - The bounding box to check
 * @param threshold - Area threshold in square degrees (default: 100)
 * @returns true if bbox area >= threshold
 */
export function shouldSplitBoundingBox(bbox: BoundingBox, threshold: number = DEFAULT_TARGET_TILE_SIZE): boolean {
  const area = calculateBoundingBoxArea(bbox);
  return area >= threshold;
}

